import React from 'react';

/**
 * Toggle switch — SIGNAL PROCESSOR hardware design.
 *
 * Track: recessed pill with inset shadow + black border.
 * Thumb: gradient metal knob with top-edge highlight.
 * Active state: emerald green.
 *
 * Uses <div role="switch"> instead of <label>+<input> to avoid
 * nested-label bugs when a parent already wraps in <label>.
 *
 * Two sizes:
 * - "md" (default): 48×24 track, 18×18 thumb
 * - "sm": 38×20 track, 14×14 thumb
 *
 * @param {Object}   props
 * @param {boolean}  props.checked   — Current state
 * @param {Function} props.onChange   — (checked: boolean) => void
 * @param {string}   [props.size]    — "md" | "sm" (default: "md")
 */
export function ToggleSwitch({ checked, onChange, size = 'md' }) {
  const isSm = size === 'sm';

  // Dimensions
  const trackW  = isSm ? 38 : 48;
  const trackH  = isSm ? 20 : 24;
  const thumbD  = isSm ? 14 : 18;
  const travel  = trackW - thumbD - 6;   // 2px inset each side + 1px border each side
  const radius  = trackH / 2;

  const trackStyle = {
    position: 'relative',
    width: trackW,
    height: trackH,
    borderRadius: radius,
    background: checked ? 'rgba(16, 185, 129, 0.6)' : '#08080a',
    border: '1px solid #000',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.05)',
    cursor: 'pointer',
    transition: 'background 0.2s',
    flexShrink: 0,
  };

  const thumbStyle = {
    position: 'absolute',
    top: 2,
    left: 2,
    width: thumbD,
    height: thumbD,
    borderRadius: '50%',
    background: 'linear-gradient(180deg, #2a2a2c 0%, #1e1e20 100%)',
    border: '1px solid #000000',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 2px 4px rgba(0,0,0,0.4)',
    transform: checked ? `translateX(${travel}px)` : 'translateX(0)',
    transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'none',
  };

  return (
    <div
      role="switch"
      aria-checked={checked}
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onChange(!checked); } }}
      style={trackStyle}
    >
      <div style={thumbStyle} />
    </div>
  );
}
